import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { counterpartyKeys } from '@/services/query-keys';
import { counterpartyService } from '@/services/counterparty/counterparty-service';
import type { CounterpartyListRow } from '@/types/counterparty.types';

const PAGE_SIZE = 100;

export interface CounterpartyRegisterParams {
  search?: string;
  is_active?: boolean;
  agreement_type?: string;
}

/**
 * Windowed counterparty register (LR-661) — the server filters and searches, we
 * pull pages on demand. Same shape as `useTradesInfinite`, whose paging notes apply.
 */
export const useCounterpartiesRegister = (params: CounterpartyRegisterParams) => {
  const query = useInfiniteQuery({
    queryKey: counterpartyKeys.register({ ...params }),
    // Row cursor, not a page index (LR-572 dp #1).
    initialPageParam: 0,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      counterpartyService.getCounterpartiesPage({
        ...params,
        // `name` is unique, so the order is total and offset paging stays stable.
        ordering: '-is_active,name',
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // An empty page ends it whatever `count` says, else one scroll to the
      // bottom loops on a drifting total (LR-572 dp #1).
      if (lastPage.results.length === 0) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    staleTime: 5 * 60 * 1000,
    // A focus refetch that failed would flip the query to `error` over cached pages.
    refetchOnWindowFocus: false,
  });

  const rows = useMemo<CounterpartyListRow[]>(
    () => (query.data?.pages ?? []).flatMap((page) => page.results),
    [query.data],
  );

  return { ...query, rows, totalCount: query.data?.pages[0]?.count };
};
